import { useCallback, useMemo, useState } from 'react';

import { LangData } from '@/constants/language';
import { useBrowserLanguage } from '@/hooks/useBrowserLanguage';
import { useBrowserTranslationDisplay } from '@/hooks/useBrowserTranslationDisplay';
import { useLanguageList } from '@/hooks/useLanguageList';
import { sendChangeDestLangEvent, sendChangeSrcLangEvent } from '@/utils/ga';

import { defaultSttLanguage, defaultTttLanguage } from './useLanguageInfo';

/**
 * プロパティ
 */
type Props = {
  // 選択された言語をローカルストレージに即時保存するか(デフォルト=false)
  isSaveOnSelectLang?: boolean;
};

/**
 * 言語選択ダイアログ hooks
 *
 * @returns
 */
export const useLanguageSelectDialog = ({
  isSaveOnSelectLang = false,
}: Props) => {
  const { isInteractive, setIsInteractive } = useBrowserTranslationDisplay();
  const { srclang, destlang, setSrclang, setDestlang } = useBrowserLanguage();
  const { srcLangList, destLangList } = useLanguageList();

  // 翻訳元言語
  const [selectedSrclangValue, setSelectedSrclangValue] = useState<string>(
    srclang || defaultSttLanguage,
  ); // キャッシュ削除後はsrclangが空なのでデフォルト言語を設定(srclangにデフォルト言語が設定される前にプルダウンメニューが表示されてリストの先頭が選択された状態になるため)
  // 翻訳先言語
  const [selectedDestlangValue, setSelectedDestlangValue] = useState<string>(
    destlang || defaultTttLanguage,
  ); // selectedSrclangValueと同じ理由
  // 指定された双方向モード(ON/OFF)
  const [isSelectedInteractiveMode, setIsSelectedInteractiveMode] =
    useState<boolean>(isInteractive);

  /**
   * 指定された言語が双方向モード対象かどうかをチェック
   */
  const checkIsInteractiveLanguage = useCallback(
    (langList: LangData[] | null, langValue: string): boolean => {
      if (!langList) {
        return false;
      }

      const langData = langList.find((value: LangData) => value[langValue]);

      if (!langData) {
        return false;
      }

      return langData[langValue].interactive;
    },
    [],
  );

  /**
   * 選択中の翻訳元言語が双方向モード対象かどうか
   */
  const isInteractiveModeSelectedSrclang = useMemo(
    () => checkIsInteractiveLanguage(srcLangList, selectedSrclangValue),
    [checkIsInteractiveLanguage, srcLangList, selectedSrclangValue],
  );

  /**
   * 選択中の翻訳先言語が双方向モード対象かどうか
   */
  const isInteractiveModeSelectedDestlang = useMemo(
    () => checkIsInteractiveLanguage(destLangList, selectedDestlangValue),
    [checkIsInteractiveLanguage, destLangList, selectedDestlangValue],
  );

  /**
   * 選択された言語が双方向モード対象外の場合、双方向モードをOFFにする
   */
  const checkAndDisableInteractiveMode = useCallback(
    (langList: LangData[], langValue: string) => {
      const langData = langList.find(
        (selectedValue: LangData) => selectedValue[langValue],
      );

      if (!langData || langData[langValue].interactive) {
        return; // 言語データが見つからない、または双方向モード対応ならスキップ
      }

      // 双方向モード対象外なので強制的にOFFにする
      setIsSelectedInteractiveMode(false);
      if (isSaveOnSelectLang) {
        setIsInteractive(false);
      }
    },
    [isSaveOnSelectLang, setIsInteractive],
  );

  /**
   * OKボタンがクリックされた時の処理
   */
  const onClickOKButton = useCallback(() => {
    // ローカルストレージに保存
    setSrclang(selectedSrclangValue);
    setDestlang(selectedDestlangValue);
    setIsInteractive(isSelectedInteractiveMode);
    // GAイベントを送信
    sendChangeSrcLangEvent({ src_lang: selectedSrclangValue });
    sendChangeDestLangEvent({ dest_lang: selectedDestlangValue });
  }, [
    setSrclang,
    selectedSrclangValue,
    setDestlang,
    selectedDestlangValue,
    setIsInteractive,
    isSelectedInteractiveMode,
  ]);

  /**
   * 翻訳元言語が変更された時の処理
   */
  const onChangeSrclang = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.currentTarget;
      setSelectedSrclangValue(value);

      // 即時保存ONの場合は選択された翻訳元言語をローカルストレージに保存
      if (isSaveOnSelectLang) {
        setSrclang(value);
      }

      checkAndDisableInteractiveMode(srcLangList, value);
    },
    [
      isSaveOnSelectLang,
      setSrclang,
      srcLangList,
      checkAndDisableInteractiveMode,
    ],
  );

  /**
   * 翻訳先言語が変更された時の処理
   */
  const onChangeDestlang = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.currentTarget;
      setSelectedDestlangValue(value);

      // 即時保存ONの場合は選択された翻訳先言語をローカルストレージに保存
      if (isSaveOnSelectLang) {
        setDestlang(value);
      }

      checkAndDisableInteractiveMode(destLangList, value);
    },
    [
      isSaveOnSelectLang,
      setDestlang,
      destLangList,
      checkAndDisableInteractiveMode,
    ],
  );

  /**
   * 双方向モードトグルON/OFFの処理
   */
  const onChangeIsInteractive = useCallback(
    (value: boolean) => {
      setIsSelectedInteractiveMode(value);

      // 即時保存ONの場合は指定された状態をローカルストレージに保存
      if (isSaveOnSelectLang) {
        setIsInteractive(value);
      }
    },
    [isSaveOnSelectLang, setIsInteractive],
  );

  return {
    // OKボタンがクリックされた時の処理
    onClickOKButton,
    // 翻訳元言語が変更された時の処理
    onChangeSrclang,
    // 翻訳先言語が変更された時の処理
    onChangeDestlang,
    // 双方向モードトグルON/OFFの処理
    onChangeIsInteractive,
    // 翻訳元言語
    selectedSrclangValue,
    // 翻訳先言語
    selectedDestlangValue,
    // 選択中の翻訳元言語が双方向モードかどうか
    isInteractiveModeSelectedSrclang,
    // 選択中の翻訳先言語が双方向モードかどうか
    isInteractiveModeSelectedDestlang,
    // 指定された双方向モード(ON/OFF)
    isInteractive: isSelectedInteractiveMode,
  };
};
